import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Modal, Button, Form, Row, Col, InputGroup } from 'react-bootstrap';
import moment from 'moment';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import 'sweetalert2/src/sweetalert2.scss';
import './AttendantCards.css';

// Same card-manager auth scope as Cards.js — this page hits the same
// /ak4tek/cards/* endpoints, just filtered to attendant cards (is_customer=0).
import { API_BASE } from '../config';
import { scopeStations } from '../components/roleAccess';
import { tr } from '../i18n';
// Auth token for every /ak4tek/cards/* call — the SAME token issued by
// /auth/login that the rest of the app uses. There is no separate card-manager
// login any more: the backend accepts a normal JWT and enforces roles and
// station scope on top of it.
const authToken = () => {
  try {
    return JSON.parse(sessionStorage.getItem('token')).data.access_token;
  } catch (e) {
    return '';
  }
};

// Turns the backend's `provisioning` array into a Swal result.
//
// A card is saved in the cloud even when a station cannot be reached — that is
// deliberate, so one offline station cannot block card creation. But it means
// "Saved" alone is misleading: the card exists here and does not work at the
// pump. Any station failure is therefore surfaced as a warning the operator has
// to dismiss, not a toast that disappears on its own.
const provisioningResult = (json, okTitle, okText) => {
  const results = Array.isArray(json.provisioning) ? json.provisioning : [];

  if (results.length === 0) {
    return { title: okTitle, text: json.message || okText, icon: 'success',
             timer: 1500, showConfirmButton: false };
  }

  const failed = results.filter((r) => !r.ok);

  if (failed.length === 0) {
    const n = results.length;
    return {
      title: okTitle,
      text: tr("{0} Sent to {1} station{2}.", json.message || okText, n, n !== 1 ? 's' : ''),
      icon: 'success',
      timer: 1800,
      showConfirmButton: false,
    };
  }

  const lines = failed
    .map((r) => `<li><b>${r.station_serial}</b> — ${r.message}</li>`)
    .join('');

  return {
    title: tr("{0} — {1} of {2} station{3} not updated", okTitle, failed.length, results.length, results.length !== 1 ? 's' : ''),
    html:
      `<p style="margin-bottom:.5rem">${tr("Saved here, but not applied at:")}</p><ul style="text-align:left;display:inline-block;margin:0">${lines}</ul><p style="margin-top:.75rem;font-size:.9em">${tr("The card will not work at those pumps until it is re-saved.")}</p>`,
    icon: 'warning',
  };
};

// station_serial can arrive as an array (current backend), a single string
// (legacy rows), or a comma-joined string — normalize to string[].
const toSerialArray = (v) =>
  Array.isArray(v)
    ? v.map((s) => String(s).trim()).filter(Boolean)
    : typeof v === 'string' && v.trim()
    ? v.split(',').map((s) => s.trim()).filter(Boolean)
    : [];

// Same wedge-reader decimal→hex conversion as Cards.js (kept local so this
// file has no dependency on Cards.js internals).
const decimalUidToHex = (dec) => {
  try {
    // eslint-disable-next-line no-undef
    let hex = BigInt(dec).toString(16).toUpperCase();
    const widths = [8, 14, 20];
    const target = widths.find((w) => hex.length <= w) || (hex.length + (hex.length % 2));
    hex = hex.padStart(target, '0');
    return hex.match(/../g).reverse().join('');
  } catch {
    return null;
  }
};
const SCAN_GAP_MS = 50;

const blankAttendantCard = {
  card_id: '',
  customer_name: '', // attendant's name — reusing the same backend field
  station_serial: [], // string[] — single station picked from a dropdown below
};

// Attendant-card add form: card_id (scan or type), name, and ONE station.
// No payment/preset/credit fields — the backend forces those defaults
// server-side whenever is_customer=0 is sent.
const AttendantFormFields = ({ formData, setFormData, stations }) => {
  const scanRef = useRef({ buffer: '', lastTime: 0 });

  const onCardIdKeyDown = (e) => {
    const s = scanRef.current;
    const now = e.timeStamp;
    if (now - s.lastTime > SCAN_GAP_MS) s.buffer = '';
    s.lastTime = now;

    if (e.key === 'Enter') {
      const raw = s.buffer;
      s.buffer = '';
      if (raw.length >= 3 && /^[0-9]+$/.test(raw)) {
        e.preventDefault();
        const hex = decimalUidToHex(raw);
        if (hex) setFormData((prev) => ({ ...prev, card_id: hex }));
      }
      return;
    }
    if (e.key.length === 1) s.buffer += e.key;
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const onStationChange = (e) => {
    const v = e.target.value;
    setFormData((prev) => ({ ...prev, station_serial: v ? [v] : [] }));
  };

  return (
    <>
      <Form.Group className="mb-3">
        <Form.Label>{tr("Card ID *")}</Form.Label>
        <Form.Control
          type="text"
          name="card_id"
          value={formData.card_id}
          onChange={onChange}
          onKeyDown={onCardIdKeyDown}
          required
          placeholder={tr("e.g. A1B2C3D4 — or tap a card on the reader")}
        />
      </Form.Group>
      <Form.Group className="mb-3">
        <Form.Label>{tr("Attendant Name *")}</Form.Label>
        <Form.Control
          type="text"
          name="customer_name"
          value={formData.customer_name}
          onChange={onChange}
          required
        />
      </Form.Group>
      <Form.Group className="mb-3">
        <Form.Label>{tr("Station *")}</Form.Label>
        <Form.Control
          as="select"
          value={formData.station_serial[0] || ''}
          onChange={onStationChange}
          required
        >
          <option value="">{tr("Select a station…")}</option>
          {stations.map((s) => (
            <option key={s.serial_number} value={s.serial_number}>
              {s.name} ({s.serial_number})
            </option>
          ))}
        </Form.Control>
      </Form.Group>
    </>
  );
};

const AttendantCardModal = ({ show, onHide, stations, cardsToken, onSaved }) => {
  const [formData, setFormData] = useState(blankAttendantCard);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (show) setFormData({ ...blankAttendantCard });
  }, [show]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.card_id.trim()) {
      Swal.fire({ title: tr("Card ID required"), icon: 'warning' });
      return;
    }
    if (!formData.customer_name.trim()) {
      Swal.fire({ title: tr("Attendant name required"), icon: 'warning' });
      return;
    }
    if (formData.station_serial.length === 0) {
      Swal.fire({ title: tr("Station required"), text: tr("Select a station for this card."), icon: 'warning' });
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API_BASE}/ak4tek/cards/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: cardsToken },
        body: JSON.stringify({
          card_id:        formData.card_id.trim(),
          customer_name:  formData.customer_name.trim(),
          station_serial: formData.station_serial,
          is_customer:    0, // pump attendant card — backend forces the rest of the defaults
        }),
      });
      const json = await response.json().catch(() => ({}));

      if (response.ok) {
        Swal.fire(provisioningResult(json, tr("Saved"), tr("Attendant card created.")));
        onSaved();
        onHide();
      } else {
        Swal.fire({ title: tr("Error {0}", response.status), text: json.message || tr("Save failed."), icon: 'error' });
      }
    } catch (err) {
      Swal.fire({ title: tr("Connection error"), text: String(err), icon: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered aria-labelledby="attendant-card-modal-title">
      <Modal.Header>
        <Modal.Title id="attendant-card-modal-title">
          <i className="fas fa-plus-circle" style={{ marginRight: '0.6em', fontSize: '1.25em', color: '#28a745' }}></i>
          {tr("Add Attendant Card")}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <AttendantFormFields formData={formData} setFormData={setFormData} stations={stations} />
        </Modal.Body>
        <Modal.Footer>
          <Button type="submit" variant="primary" disabled={saving}>
            {saving ? tr("Saving…") : tr("Create Card")}
          </Button>
          <Button onClick={onHide} disabled={saving}>
            {tr("Close")}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

const AttendantCards = ({ token, LogoutTimeout }) => {
  const [cards, setCards] = useState([]);
  const [stations, setStations] = useState([]);
  const [stationFilter, setStationFilter] = useState('');
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(false);
  const cardsToken = authToken();
  const [addShow, setAddShow] = useState(false);
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  const handleSort = (key) => {
    if (sortBy === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir('asc');
    }
  };


  const fetchStations = useCallback(async () => {
    try {
      const tok = JSON.parse(sessionStorage.getItem('token')).data.access_token;
      const r = await fetch(`${API_BASE}/station/all`, { headers: { Authorization: tok } });
      const j = await r.json();
      if (j && j.data) {
        const vfdStations = j.data.filter((s) => s.is_vfd).map((s) => ({ name: s.name, serial_number: s.serial_number }));
        setStations(scopeStations(token, vfdStations));
      }
    } catch (err) {
      console.warn('Could not fetch stations:', err);
    }
  }, [token]);

  // Same /all + /?station_serial=X endpoints as Cards.js — filtered down to
  // is_customer === 0 client-side, since the backend has no separate route
  // for attendant-only cards.
  const fetchCards = useCallback(
    async (filterSerial) => {
      setLoading(true);
      const tok = cardsToken;
      if (!tok) {
        setLoading(false);
        return;
      }

      const url = filterSerial
        ? `${API_BASE}/ak4tek/cards/?station_serial=${encodeURIComponent(filterSerial)}`
        : `${API_BASE}/ak4tek/cards/all`;

      // A 401 here means the session has expired. There is no silent
      // re-login to fall back on any more, which is the point — the user is
      // sent back through the normal login like every other page.
      const response = await fetch(url, { headers: { Authorization: tok } });

      try {
        const j = await response.json();
        if (response.ok && j && Array.isArray(j.data)) {
          setCards(j.data.filter((c) => Number(c.is_customer) === 0));
        } else {
          setCards([]);
        }
      } catch {
        setCards([]);
      }
      setLoading(false);
    },
    [cardsToken]
  );

  useEffect(() => {
    fetchStations();
  }, [fetchStations]);

  useEffect(() => {
    fetchCards(stationFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stationFilter, cardsToken]);

  const handleDelete = async (card) => {
    const result = await Swal.fire({
      title: tr("Delete this card?"),
      html: `<b>${card.card_id}</b><br/>${card.customer_name || ''}<br/><small>${toSerialArray(card.station_serial).join(', ')}</small>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: tr("Yes, delete"),
      cancelButtonText: tr("Cancel"),
      confirmButtonColor: '#d33',
    });
    if (!result.isConfirmed) return;

    try {
      const r = await fetch(
        `${API_BASE}/ak4tek/cards/${encodeURIComponent(card.card_id)}`,
        { method: 'DELETE', headers: { Authorization: cardsToken } }
      );
      const j = await r.json().catch(() => ({}));
      if (r.ok) {
        Swal.fire(provisioningResult(j, tr("Deleted"), tr("Card removed.")));
        fetchCards(stationFilter);
      } else {
        Swal.fire({ title: tr("Error {0}", r.status), text: j.message || tr("Delete failed."), icon: 'error' });
      }
    } catch (err) {
      Swal.fire({ title: tr("Connection error"), text: String(err), icon: 'error' });
    }
  };

  const lc = searchText.trim().toLowerCase();
  const visibleCards = lc
    ? cards.filter(
        (c) =>
          (c.card_id || '').toLowerCase().includes(lc) ||
          (c.customer_name || '').toLowerCase().includes(lc) ||
          toSerialArray(c.station_serial).join(' ').toLowerCase().includes(lc)
      )
    : cards;

  const stationName = (serial) => {
    const s = stations.find((x) => x.serial_number === serial);
    return s ? s.name : serial;
  };
  const stationNames = (card) => toSerialArray(card.station_serial).map(stationName).join(', ');

  const sortedCards = sortBy
    ? [...visibleCards].sort((a, b) => {
        const dir = sortDir === 'asc' ? 1 : -1;
        let av, bv;
        switch (sortBy) {
          case 'card_id':       av = a.card_id || '';                       bv = b.card_id || '';                       break;
          case 'customer_name': av = a.customer_name || '';                 bv = b.customer_name || '';                 break;
          case 'station':       av = stationNames(a);                      bv = stationNames(b);                       break;
          case 'created_at':    av = new Date(a.created_at || 0).getTime(); bv = new Date(b.created_at || 0).getTime(); break;
          default: return 0;
        }
        if (typeof av === 'string') return av.localeCompare(bv) * dir;
        return (av < bv ? -1 : av > bv ? 1 : 0) * dir;
      })
    : visibleCards;

  const sortHeader = (label, key) => {
    const isActive = sortBy === key;
    const arrow = isActive ? (sortDir === 'asc' ? '▲' : '▼') : '↕';
    return (
      <th onClick={() => handleSort(key)} style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }} title={tr("Click to sort")}>
        {label}
        <span style={{ marginLeft: 6, opacity: isActive ? 1 : 0.3, fontSize: '0.75em' }}>{arrow}</span>
      </th>
    );
  };

  return (
    <div className="AttendantCardsPage">
      <div className="FilterBar">
        <h5 className="FilterBarTitle">{tr("Pump Attendant Cards")}</h5>
        <Row style={{ alignItems: 'flex-end' }}>
          <Col md={4}>
            <Form.Group className="mb-0">
              <Form.Label>{tr("Filter by station")}</Form.Label>
              <Form.Control as="select" value={stationFilter} onChange={(e) => setStationFilter(e.target.value)}>
                <option value="">{tr("All stations")}</option>
                {stations.map((s) => (
                  <option key={s.serial_number} value={s.serial_number}>
                    {s.name} ({s.serial_number})
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-0">
              <Form.Label>{tr("Search")}</Form.Label>
              <InputGroup>
                <InputGroup.Text>
                  <i className="fas fa-search" ></i>
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder={tr("Card ID or name…")}
                />
              </InputGroup>
            </Form.Group>
          </Col>
          <Col md={4} style={{ textAlign: 'right' }}>
            <Button variant="primary" onClick={() => setAddShow(true)}>
              {tr("+ Add Attendant Card")}
            </Button>{' '}
            <Button variant="outline-secondary" onClick={() => fetchCards(stationFilter)} disabled={loading}>
              {loading ? tr("Loading…") : tr("Refresh")}
            </Button>
          </Col>
        </Row>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table className="table table-striped table-bordered table-hover">
          <thead className="thead-dark">
            <tr>
              {sortHeader(tr("Card ID"), 'card_id')}
              {sortHeader(tr("Name"), 'customer_name')}
              {sortHeader(tr("Station"), 'station')}
              {sortHeader(tr("Created"), 'created_at')}
              <th>{tr("Actions")}</th>
            </tr>
          </thead>
          <tbody>
            {sortedCards.length === 0 && !loading && (
              <tr>
                <td colSpan="5" style={{ padding: '3em 1em' }}>
                  <div style={{ textAlign: 'center', color: '#888' }}>
                    <i className="fas fa-id-badge" style={{ fontSize: '3em', color: '#ccc', marginBottom: '0.5em' }} />
                    <div style={{ marginBottom: '1em', fontSize: '1.05em' }}>
                      {searchText || stationFilter ? tr("No cards match your filter.") : tr("No attendant cards yet — add one to get started.")}
                    </div>
                    {!searchText && !stationFilter && (
                      <Button variant="primary" onClick={() => setAddShow(true)}>
                        <i className="fas fa-plus-circle" style={{ marginRight: '0.5em' }} />
                        {tr("Add your first attendant card")}
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            )}
            {sortedCards.map((c) => (
              <tr key={c.id || c.card_id}>
                <td>
                  <span className="card-id">{c.card_id}</span>
                </td>
                <td>{c.customer_name}</td>
                <td title={toSerialArray(c.station_serial).join(', ')}>
                  <div className="d-flex flex-wrap" style={{ gap: '0.3rem' }}>
                    {toSerialArray(c.station_serial).map((s) => (
                      <span key={s} className="badge bg-light text-dark border" title={s}>
                        {stationName(s)}
                      </span>
                    ))}
                  </div>
                </td>
                <td>
                  <small title={c.created_at}>{c.created_at ? moment(c.created_at).format('MMM D, YYYY h:mm A') : '—'}</small>
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <Button variant="danger" size="sm" onClick={() => handleDelete(c)} title={tr("Delete card")}>
                    <i className="fas fa-trash"></i>
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AttendantCardModal
        show={addShow}
        onHide={() => setAddShow(false)}
        stations={stations}
        cardsToken={cardsToken}
        onSaved={() => fetchCards(stationFilter)}
      />
    </div>
  );
};

export default AttendantCards;